// ── Sidebar Snapshot UI ──────────────────────────────────────────
// Manages the sidebar snapshot list: rendering, save, restore, delete.

import { getState, setState } from './state.js';
import { listingFacade } from './interaction/facades/listing.facade.instance.js';
import { batchFacade } from './interaction/facades/batch.facade.instance.js';
import { syncNavigationBounds } from './navigation-bounds.js';
import { showToast } from './toast.js';
import { buildListingDraftPayload, restoreListingDraftPayload } from './listing-draft-payload.js';
import { resolveBatchRestoreContext } from './interaction/builders/batch-restore-context.js';
import { resetStep3AutoSaveScope } from './step3/autosave.js';

const $ = (sel) => document.querySelector(sel);
const $$ = (sel) => document.querySelectorAll(sel);
function createSnapshotElement(tagName, className, text) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined && text !== null) element.textContent = text;
  return element;
}
const DEBUG_CLIENT = localStorage.getItem('debug_client') === '1';
function clientDebug(...args) {
  if (!DEBUG_CLIENT) return;
  console.debug(...args);
}

// ── Render ───────────────────────────────────────────────────────

function renderSnapshotList(snapshots, currentJobId) {
  const section = $('#snapshot-sidebar-section');
  const list = $('#snapshot-list');
  if (!section || !list) return;

  section.hidden = false;

  if (!snapshots || snapshots.length === 0) {
    list.replaceChildren(createSnapshotElement('div', 'snapshot-empty', '暂无快照'));
    return;
  }

  list.replaceChildren(...snapshots.map((snapshot) => {
    const jobId = snapshot.job_id || snapshot.jobId; // 数据库字段是 job_id
    const createdAt = snapshot.created_at || snapshot.createdAt; // 数据库字段是 created_at
    const isActive = jobId === currentJobId;
    const formattedDate = createdAt
      ? new Date(createdAt).toLocaleString('zh-CN', {
          timeZone: 'Asia/Shanghai',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '';
    const item = createSnapshotElement('div', `snapshot-item${isActive ? ' active' : ''}`);
    item.dataset.id = String(snapshot.id || '');
    const name = createSnapshotElement('div', 'snapshot-item-name', snapshot.name);
    name.title = String(snapshot.name || '');
    const actions = createSnapshotElement('div', 'snapshot-item-actions');
    const restore = createSnapshotElement('button', 'snapshot-action-btn restore', '恢复');
    restore.type = 'button';
    restore.dataset.id = String(snapshot.id || '');
    restore.title = '恢复此快照';
    const remove = createSnapshotElement('button', 'snapshot-action-btn delete', '删除');
    remove.type = 'button';
    remove.dataset.id = String(snapshot.id || '');
    remove.title = '删除此快照';
    actions.append(restore, remove);
    item.append(name, createSnapshotElement('div', 'snapshot-item-meta', `${formattedDate} · Step ${snapshot.step || '?'}`), actions);
    return item;
  }));

  // Bind restore
  list.querySelectorAll('.snapshot-action-btn.restore').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = btn.dataset.id;
      if (confirm('确定要恢复此快照吗？当前未保存的数据将丢失。')) {
        await restoreSnapshot(id);
      }
    });
  });

  // Bind delete
  list.querySelectorAll('.snapshot-action-btn.delete').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = btn.dataset.id;
      if (confirm('确定删除此快照？')) {
        await deleteSnapshot(id);
      }
    });
  });

  // Bind item click → restore
  list.querySelectorAll('.snapshot-item').forEach((item) => {
    item.addEventListener('click', () => {
      const id = item.dataset.id;
      if (confirm('确定要恢复此快照吗？当前未保存的数据将丢失。')) {
        restoreSnapshot(id);
      }
    });
  });
}

// ── Actions ───────────────────────────────────────────────────────

async function saveSnapshot() {
  const jobId = getState('batchJobId');
  if (!jobId) {
    showToast('请先运行批量分析任务后再保存快照。', { type: 'error', id: 'snapshot-toast' });
    return;
  }

  const step = getState('currentStep');
  const bj = new Date().toLocaleString('sv-SE', { timeZone: 'Asia/Shanghai' }); // YYYY-MM-DD HH:mm:ss
  const name = `快照_${bj.slice(0, 16).replace(' ', '_').replace(/-/g, '').replace(':', '')}`;

  // Collect all state across all steps
  const selectedStKeywords = [...(getState('selectedStKeywords') || new Set())];
  const marketDataKeywords = getState('marketDataKeywords') || getState('mcpKeywords') || [];
  const keywordLibrary = getState('keywordLibrary');
  const snapshotState = getState('snapshotState');
  const productInfo = getState('productInfo');
  const businessContext = getState('businessContext');
  const keywordUsePolicies = getState('keywordUsePolicies');
  const competitorRoles = getState('competitorRoles');
  
  const draftPayload = buildListingDraftPayload();
  const complianceResult = getState('complianceResult');
  const algorithmTips = getState('algorithmTips');
  const scoringReport = getState('scoringReport');
  const revisionSuggestions = getState('revisionSuggestions');
  const revisionOverview = getState('revisionOverview');

  try {
    const data = await listingFacade.createSnapshot({
      jobId,
      step,
      name,
      state: {
        selectedStKeywords,
        marketplace: getState('marketplace') || productInfo?.marketplace || null,
        diagnosticMarketplace: getState('diagnosticMarketplace') || null,
        marketDataKeywords,
        mcpKeywords: marketDataKeywords,
        keywordLibrary,
        businessContext,
        keywordUsePolicies,
        competitorRoles,
        snapshotState,
        productInfo,
        ...draftPayload,
        complianceResult,
        algorithmTips,
        scoringReport,
        revisionSuggestions,
        revisionOverview,
      },
    });
    if (data.error) throw new Error(data.error);
    await refreshSnapshots();
    showToast('快照已保存', { id: 'snapshot-toast' });
  } catch (err) {
    showToast(`保存快照失败: ${err.message}`, { type: 'error', id: 'snapshot-toast' });
  }
}

async function restoreSnapshot(id) {
  try {
    const data = await listingFacade.getSnapshot(id);
    if (data.error) throw new Error(data.error);

    const snapshot = data.snapshot;
    if (!snapshot) throw new Error('快照不存在');

    // Parse stored state from snapshot
    const state = typeof snapshot.state === 'string'
      ? JSON.parse(snapshot.state)
      : (snapshot.state || {});

    // Build stepReady based on which step was snapshotted
    const ready = {};
    for (let i = 1; i <= (snapshot.step || 2); i++) ready[i] = true;

    // 🔥 关键修复：逐个更新状态，触发每个字段的 Changed 事件
    // 第一步：更新基础状态
    const jobId = snapshot.job_id || snapshot.jobId; // 数据库字段是 job_id
    clientDebug('[Snapshot Restore] jobId:', jobId);
    let batchJob = null;
    try {
      batchJob = await batchFacade.getBatchJob(jobId);
    } catch (err) {
      clientDebug('[Snapshot Restore] 无法读取批任务当前上下文:', err.message);
    }
    const restoreContext = resolveBatchRestoreContext({ batchJob, savedState: state });

    // 快照恢复会切换 Listing 任务上下文。先撤销旧 job 自动保存的回写权，
    // 再清空不属于快照草稿的运行时关联；恢复的商品不得继承上一商品
    // 的落库记录、生成任务或优化中心总指令。
    resetStep3AutoSaveScope();
    setState('savedListingId', null);
    setState('activeListingGenerationTaskId', '');
    setState('masterRevisionInstructions', {
      general: '',
      title: '',
      bullets: '',
      description: '',
      aplus: '',
      qa: '',
      searchTerms: '',
    });
    setState('batchJobId', jobId);
    setState('batchJob', restoreContext.batchJob);
    setState('keywordLibrary', restoreContext.keywordLibrary);
    setState('businessContext', restoreContext.businessContext);
    setState('keywordUsePolicies', restoreContext.keywordUsePolicies);
    setState('competitorRoles', restoreContext.competitorRoles);
    if (restoreContext.marketplace) setState('marketplace', restoreContext.marketplace);
    setState('batchSnapshots', restoreContext.batchSnapshots);
    setState('competitorAsinsIncluded', new Set(restoreContext.batchSnapshots.map((item) => item.asin)));
    setState('selectedCompetitorIndex', 0);
    setState('snapshotState', restoreContext.snapshotState);
    setState('productInfo', restoreContext.productInfo);
    setState('selectedStKeywords', new Set(state.selectedStKeywords || []));
    setState('step02SearchTermsSelectionDraft', null);
    const marketDataKeywords = state.marketDataKeywords || state.mcpKeywords || [];
    setState('marketDataKeywords', marketDataKeywords);
    setState('mcpKeywords', marketDataKeywords);

    // 第二步：根据 step 更新或清空数据
    if (snapshot.step >= 3) {
      restoreListingDraftPayload(state);
    } else {
      setState('listingDraftTitle', null);
      setState('listingDraftBullets', null);
      setState('listingDraftDescription', null);
      setState('listingDraftSearchTerms', null);
      setState('listingDraftQa', null);
      setState('listingDraftV1', null);
      setState('optimizerResult', null);
    }

    if (snapshot.step >= 4) {
      const restoredDiagnosticMarketplace = String(state.diagnosticMarketplace || '').trim().toUpperCase();
      const diagnosticMatchesMarketplace = restoredDiagnosticMarketplace
        && restoredDiagnosticMarketplace === restoreContext.marketplace;
      setState('diagnosticMarketplace', diagnosticMatchesMarketplace ? restoredDiagnosticMarketplace : null);
      setState('complianceResult', diagnosticMatchesMarketplace ? (state.complianceResult || null) : null);
      setState('algorithmTips', diagnosticMatchesMarketplace ? (state.algorithmTips || null) : null);
    } else {
      setState('diagnosticMarketplace', null);
      setState('complianceResult', null);
      setState('algorithmTips', null);
    }

    if (snapshot.step >= 5) {
      setState('scoringReport', getState('diagnosticMarketplace') ? (state.scoringReport || null) : null);
    } else {
      setState('scoringReport', null);
    }

    if (snapshot.step >= 6) {
      const hasTrustedDiagnostic = Boolean(getState('diagnosticMarketplace'));
      setState('revisionSuggestions', hasTrustedDiagnostic ? (state.revisionSuggestions || null) : null);
      setState('revisionOverview', hasTrustedDiagnostic ? (state.revisionOverview || null) : null);
    } else {
      setState('revisionSuggestions', null);
      setState('revisionOverview', null);
    }

    // 第三步：更新导航状态（最后更新，触发 UI 切换）
    setState('stepReady', ready);
    syncNavigationBounds();
    const target = snapshot.step || 2;
    const maxNav = getState('highestStep');
    setState('currentStep', Math.min(target, maxNav));

    // Refresh sidebar
    await refreshSnapshots();

    // 🔥 不刷新页面，只显示提示
    const stepName = ['', '批量分析', '关键词库', 'Listing生成', '合规检测', '质量打分', '修订建议'][snapshot.step] || `Step ${snapshot.step}`;
    showToast(`快照已恢复到【${stepName}】`, { id: 'snapshot-toast' });
  } catch (err) {
    console.error('恢复快照失败:', err);
    showToast(`恢复快照失败: ${err.message}`, { type: 'error', id: 'snapshot-toast' });
  }
}

async function deleteSnapshot(id) {
  try {
    const data = await listingFacade.deleteSnapshot(id);
    if (data.error) throw new Error(data.error);
    await refreshSnapshots();
  } catch (err) {
    showToast(`删除快照失败: ${err.message}`, { type: 'error', id: 'snapshot-toast' });
  }
}

// ── Load & Refresh ───────────────────────────────────────────────

async function refreshSnapshots() {
  try {
    const data = await listingFacade.listSnapshots();
    const snapshots = data.snapshots || [];
    const currentJobId = getState('batchJobId');
    renderSnapshotList(snapshots, currentJobId);
  } catch {
    // Silently fail — snapshot section is non-critical
  }
}


// ── Init ─────────────────────────────────────────────────────────

export function initSidebarSnapshots() {
  // Bind save snapshot button (sidebar)
  $('#save-snapshot-btn')?.addEventListener('click', () => {
    const jobId = getState('batchJobId');
    if (!jobId) {
      showToast('请先运行批量分析任务后再保存快照。', { type: 'error', id: 'snapshot-toast' });
      return;
    }
    saveSnapshot();
  });

  // Bind floating snapshot button
  $('#floating-snapshot-btn')?.addEventListener('click', () => {
    const jobId = getState('batchJobId');
    if (!jobId) {
      showToast('请先运行批量分析任务后再保存快照。', { type: 'error', id: 'snapshot-toast' });
      return;
    }
    saveSnapshot();
  });

  // On step changes, refresh snapshot list
  // (snapshots are scoped to user, so always show current user's list)
  // Refresh on load
  refreshSnapshots();
}
